// Single source-of-truth for ERP role → module access on the frontend.
// A module key maps to a menu item / route defined in ERPLayout menuSections
// and ProtectedRoute routePermissions.

export const ALL_MODULES = [
  'superadmin', 'superadmin_users', 'superadmin_audit', 'superadmin_permissions', 'superadmin_credentials',
  'admin', 'inquiries', 'floormanager', 'customermaster', 'rewards',
  'orders', 'salesinvoice', 'purchasemodule', 'quotations', 'discountapprovals',
  'leads', 'abandonedcarts', 'salestasks', 'salestargets', 'incentives',
  'performance', 'sfa', 'crm',
  'production', 'jobcards', 'operator', 'inventory', 'transport', 'driverdashboard', 'jobwork',
  'hr', 'onboarding', 'hrletters', 'leave', 'holidays', 'assets',
  'accounts', 'payments', 'ledger', 'payouts', 'expenses', 'gst', 'vendors',
  'forecast', 'cms', 'branches', 'productconfig', 'glassweight',
  'tally', 'settings',
  'refunds',
];

// Modules that are exclusively for the super_admin account.
const SUPERADMIN_ONLY_MODULES = [
  'superadmin', 'superadmin_users', 'superadmin_audit', 'superadmin_permissions', 'superadmin_credentials',
];

// Canonical roles and the modules (menu items / routes) they are allowed to open.
// Each role is intentionally restricted to its OWN department's modules so the
// sidebar only ever shows that role's core functions.
export const ROLE_MODULES = {
  // ── Management ────────────────────────────────────────────────────────────
  // Admins / owner / manager see the whole system (appropriate for management).
  super_admin: ALL_MODULES,
  admin: ALL_MODULES.filter((m) => !SUPERADMIN_ONLY_MODULES.includes(m)),
  owner: ALL_MODULES.filter((m) => !SUPERADMIN_ONLY_MODULES.includes(m)),
  manager: [
    'admin', 'inquiries', 'floormanager', 'customermaster', 'rewards',
    'orders', 'salesinvoice', 'purchasemodule', 'quotations', 'discountapprovals',
    'leads', 'abandonedcarts', 'salestasks', 'salestargets', 'incentives',
    'performance', 'sfa', 'crm',
    'production', 'jobcards', 'operator', 'inventory', 'transport', 'jobwork',
    'hr', 'onboarding', 'hrletters', 'leave', 'holidays', 'assets',
    'accounts', 'payments', 'ledger', 'payouts', 'expenses', 'gst', 'vendors', 'refunds',
    'forecast', 'cms', 'branches', 'productconfig', 'glassweight', 'tally', 'settings',
  ],

  // ── Finance (Finance section only) ────────────────────────────────────────
  finance: [
    'accounts', 'payments', 'ledger', 'payouts', 'expenses', 'gst', 'vendors',
    'salesinvoice', 'refunds',
  ],

  // ── HR & Admin (HR & Admin section only) ──────────────────────────────────
  hr: ['hr', 'onboarding', 'hrletters', 'leave', 'holidays', 'assets'],

  // ── Sales & CRM (Sales & CRM section only) ────────────────────────────────
  // Customer Master and Rewards & Referrals were pulled out of Sales: Customer
  // Master now sits in Management, and Rewards belongs to the wallet/referral
  // system that only Management administers.
  sales_manager: [
    'crm', 'orders', 'quotations', 'discountapprovals', 'leads', 'abandonedcarts',
    'salestasks', 'salestargets', 'incentives', 'performance', 'sfa', 'salesinvoice',
  ],
  sales: ['crm', 'sfa', 'quotations', 'leads', 'salestasks', 'abandonedcarts', 'salesinvoice'],

  // ── Operations (Operations section only) ──────────────────────────────────
  // production_manager + transport_manager removed — both merged into floor_manager
  floor_manager: ['production', 'jobcards', 'operator', 'inventory', 'floormanager', 'transport', 'jobwork'],
  operator: ['operator'],
  transport: ['transport'],
  driver: ['driverdashboard'],
  store_manager: ['inventory', 'assets'],

  // ── Read-only finance oversight (Finance section only) ────────────────────
  auditor: ['ledger', 'accounts', 'payments', 'gst', 'refunds'],

  // ── External accounts ─────────────────────────────────────────────────────
  customer: [],
  dealer: ['crm', 'quotations'],
};

// Legacy / historical roles are resolved to a canonical role so old accounts
// keep behaving sensibly without bloating the supported role list.
export const ROLE_ALIASES = {
  accounts: 'finance',
  accountant: 'finance',
  sales_executive: 'sales',
  store: 'store_manager',
  logistics: 'transport',
  production_manager: 'floor_manager',
  transport_manager: 'floor_manager',
  supervisor: 'floor_manager',
  production: 'floor_manager',
  ca: 'auditor',
};

// Resolve the module list for a role (handles aliases + unknown roles safely).
export const resolveRoleModules = (role) => {
  const key = (role || 'customer').toLowerCase();
  if (ROLE_MODULES[key]) return ROLE_MODULES[key];
  const canonical = ROLE_ALIASES[key];
  if (canonical && ROLE_MODULES[canonical]) return ROLE_MODULES[canonical];
  return [];
};

// Resolve the effective module list for a user:
// - An explicit, non-empty module_permissions[] override wins (superadmin-granted).
// - null / missing / empty list falls back to the role's defaults.
export const resolveModulesForUser = (user) => {
  const overrides = Array.isArray(user?.module_permissions)
    ? user.module_permissions.filter(Boolean)
    : [];
  if (overrides.length > 0) return overrides;
  return resolveRoleModules(user?.role);
};

export const isAdminLike = (role) => ['super_admin', 'admin', 'owner'].includes(role);
